import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../api/client'
import { reconciliacionApi, type ReconciliacionBanding } from '../api/reconciliacion'
import { Table, type Column } from '../components/ui/Table'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { GenericBadge } from '../components/ui/Badge'
import { Card, CardHeader } from '../components/ui/Card'
import { fixed, num } from '../utils/format'

type TipoTanque = 'GRANEL' | 'RETAIL'

interface Tanque {
  id: string
  nombre: string
  tipo: TipoTanque
  ubicacionLat: number | null
  ubicacionLng: number | null
  capacidadLitros: number | null
  stockActual: number
  precioPorLitro: number
  moneda: string
  sensorImei: string | null
  activo: boolean
  creadoAt: string
}

function useTanques() {
  return useQuery({
    queryKey: ['tanques'],
    queryFn: () => apiClient.get<{ items: Tanque[] }>('/tanques').then((r) => r.data),
  })
}

function stockPct(stockActual: number, capacidadLitros: number | null) {
  if (!capacidadLitros || capacidadLitros === 0) return null
  return Math.round((stockActual / capacidadLitros) * 100)
}

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10)
}

function defaultRange(): { desde: string; hasta: string } {
  const hasta = new Date()
  const desde = new Date()
  desde.setDate(desde.getDate() - 30)
  return { desde: isoDate(desde), hasta: isoDate(hasta) }
}

const BANDING_STYLES: Record<ReconciliacionBanding, { bg: string; text: string; label: string }> = {
  OK:       { bg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300', label: 'OK' },
  WARNING:  { bg: 'bg-orange-50 dark:bg-orange-900/20',  text: 'text-orange-700 dark:text-orange-300',   label: 'AVISO' },
  CRITICAL: { bg: 'bg-red-50 dark:bg-red-900/20',        text: 'text-red-700 dark:text-red-300',         label: 'CRITICO' },
}

function ReconciliacionPanel({ tanqueId }: { tanqueId: string }) {
  const navigate = useNavigate()
  const [range, setRange] = useState(defaultRange)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reconciliacion', tanqueId, range.desde, range.hasta],
    queryFn: () => reconciliacionApi.getByTanque(tanqueId, range.desde, range.hasta),
    enabled: !!tanqueId,
  })

  const banding = data?.banding ?? 'OK'
  const style = BANDING_STYLES[banding]

  return (
    <div className="border-t border-slate-100 dark:border-slate-700 px-5 py-4 bg-slate-50 dark:bg-slate-800/50">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <h4 className="font-semibold text-sm text-slate-700 dark:text-slate-300 mr-2">Reconciliacion de inventario</h4>
        <Input
          type="date"
          label="Desde"
          value={range.desde}
          onChange={(e) => setRange((p) => ({ ...p, desde: e.target.value }))}
        />
        <Input
          type="date"
          label="Hasta"
          value={range.hasta}
          onChange={(e) => setRange((p) => ({ ...p, hasta: e.target.value }))}
        />
      </div>

      {isLoading && (
        <div className="text-sm text-slate-400 animate-pulse">Cargando...</div>
      )}

      {isError && (
        <div className="text-sm text-red-600 dark:text-red-400">
          Error al cargar reconciliacion. Verifique conexion al backend.
          {/* TODO: handle error — mostrar mock si quieres un fallback local */}
        </div>
      )}

      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Stock inicial</p>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                {num(data.stockInicial, 0)} L
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Dispensado</p>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                {num(data.dispensado, 0)} L
              </p>
              <p className="text-xs text-slate-400 mt-0.5">{data.cargasCount} cargas</p>
            </div>

            <div className={`rounded-xl border p-3 ${style.bg} border-transparent`}>
              <p className={`text-xs uppercase tracking-wide mb-1 font-semibold ${style.text}`}>Varianza</p>
              <p className={`text-2xl font-bold tabular-nums ${style.text}`}>
                {data.variancePct >= 0 ? '+' : ''}{fixed(data.variancePct, 2)}%
              </p>
              <p className={`text-xs mt-0.5 font-bold ${style.text}`}>{style.label}</p>
            </div>

            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Delta litros</p>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                {data.variance >= 0 ? '+' : ''}{num(data.variance, 1)} L
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Esperado {num(data.expected, 0)} / Real {num(data.actual, 0)}
              </p>
            </div>
          </div>

          {data.alerta && (
            <div className="flex items-start gap-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 px-4 py-2.5 text-sm text-yellow-800 dark:text-yellow-300">
              <span>⚠</span>
              <span>{data.alerta}</span>
            </div>
          )}

          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/cargas?desde=${range.desde}&hasta=${range.hasta}&tanqueId=${tanqueId}`)}
          >
            Ver cargas del periodo
          </Button>
        </div>
      )}
    </div>
  )
}

export default function Tanques() {
  const { data, isLoading } = useTanques()
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const COLUMNS: Column<Tanque>[] = [
    {
      key: 'nombre',
      header: 'Nombre',
      sortable: true,
      render: (r) => <span className="font-medium">{r.nombre}</span>,
    },
    {
      key: 'tipo',
      header: 'Tipo',
      render: (r) => <GenericBadge label={r.tipo} variant={r.tipo === 'GRANEL' ? 'info' : 'default'} />,
    },
    {
      key: 'stock',
      header: 'Stock actual',
      render: (r) => {
        const pct = stockPct(r.stockActual, r.capacidadLitros)
        const variant = pct !== null && pct < 20 ? 'danger' : pct !== null && pct < 40 ? 'warning' : 'success'
        return (
          <div className="flex items-center gap-2">
            <span className="tabular-nums">{Number(r.stockActual).toFixed(0)} L</span>
            {pct !== null && <GenericBadge label={`${pct}%`} variant={variant} />}
          </div>
        )
      },
    },
    {
      key: 'capacidad',
      header: 'Capacidad',
      render: (r) => (r.capacidadLitros ? `${r.capacidadLitros} L` : '—'),
      className: 'tabular-nums',
    },
    {
      key: 'precio',
      header: 'Precio/L',
      render: (r) => `${r.moneda} ${Number(r.precioPorLitro).toFixed(4)}`,
      className: 'tabular-nums',
    },
    { key: 'sensor', header: 'Sensor', render: (r) => r.sensorImei ?? <span className="text-slate-400">—</span> },
    {
      key: 'activo',
      header: 'Estado',
      render: (r) => <GenericBadge label={r.activo ? 'Activo' : 'Inactivo'} variant={r.activo ? 'success' : 'default'} />,
    },
    {
      key: 'reconciliacion',
      header: '',
      render: (r) =>
        r.tipo === 'GRANEL' ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setExpandedId((prev) => (prev === r.id ? null : r.id))
            }}
            className="text-xs text-primary hover:underline font-medium whitespace-nowrap"
          >
            {expandedId === r.id ? 'Cerrar' : 'Reconciliacion'}
          </button>
        ) : null,
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Tanques</h1>
        <Button size="sm" disabled>+ Nuevo tanque (proximamente)</Button>
      </div>

      {!isLoading &&
        (data?.items ?? []).some((t) => {
          const pct = stockPct(t.stockActual, t.capacidadLitros)
          return pct !== null && pct < 20
        }) && (
          <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
            <CardHeader title="Alerta de stock bajo" subtitle="Tanques con menos del 20% de capacidad" />
            <div className="space-y-1">
              {(data?.items ?? [])
                .filter((t) => {
                  const p = stockPct(t.stockActual, t.capacidadLitros)
                  return p !== null && p < 20
                })
                .map((t) => (
                  <p key={t.id} className="text-sm text-red-700 dark:text-red-300">
                    {t.nombre}: {Number(t.stockActual).toFixed(0)} L ({stockPct(t.stockActual, t.capacidadLitros)}%)
                  </p>
                ))}
            </div>
          </Card>
        )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <Table
          columns={COLUMNS}
          data={data?.items ?? []}
          keyExtractor={(r) => r.id}
          loading={isLoading}
          emptyMessage="Sin tanques registrados"
        />

        {/* Reconciliation expand rows */}
        {(data?.items ?? [])
          .filter((t) => t.tipo === 'GRANEL' && t.id === expandedId)
          .map((t) => (
            <ReconciliacionPanel key={t.id} tanqueId={t.id} />
          ))}
      </div>
    </div>
  )
}
